import { createORPCClient, ORPCError } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import { SimpleCsrfProtectionLinkPlugin } from '@orpc/client/plugins';
import { createORPCVueQueryUtils } from '@orpc/vue-query';
import { TENANT_HEADER, type ApiClient } from '@platform/contracts';
import { ref } from 'vue';

// The typed client of the tenant API. Which tenant a call is about is not part of any input: the
// page sets it once (from the address bar) and every call carries it in a header.

/** The tenant the page is showing. Set by the app layout before any tenant query runs. */
export const currentTenantId = ref<string | undefined>();

const link = new RPCLink({
  // The link needs an absolute URL; the API is always on the page's own origin.
  url: `${window.location.origin}/api/rpc`,
  headers: () => (currentTenantId.value === undefined ? {} : { [TENANT_HEADER]: currentTenantId.value }),
  // A header only our own script can add: a session cookie alone is not enough to call the API.
  plugins: [new SimpleCsrfProtectionLinkPlugin()],
});

export const api: ApiClient = createORPCClient(link);
export const orpc = createORPCVueQueryUtils(api);

export interface Problem {
  /** The API's error code, e.g. `FORBIDDEN`; `UNKNOWN` for anything that is not an API answer. */
  code: string;
  /** A message fit to show as is. */
  message: string;
  /** Messages per input path, for a validation failure. */
  fields: Record<string, string[]>;
}

/** Turns whatever a call threw into something a page can show. */
export function problemOf(error: unknown): Problem {
  if (error instanceof ORPCError) {
    const fields: Record<string, string[]> = {};
    const data: unknown = error.data;
    if (typeof data === 'object' && data !== null && 'fieldErrors' in data && typeof data.fieldErrors === 'object' && data.fieldErrors !== null) {
      for (const [key, value] of Object.entries(data.fieldErrors)) {
        if (Array.isArray(value)) fields[key] = value.filter((item): item is string => typeof item === 'string');
      }
    }
    // cast: an error's code is a string; the class is generic over it.
    return { code: error.code as string, message: error.message, fields };
  }
  return { code: 'UNKNOWN', message: error instanceof Error ? error.message : String(error), fields: {} };
}
